import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Toolbar,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
  IconButton,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../../../utils/Api";
import SearchRounded from "@mui/icons-material/SearchRounded";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import { KeyboardArrowLeft, LocalMallOutlined } from "@mui/icons-material";
import Pagination from "@mui/material/Pagination";
import { useNavigate } from "react-router-dom";

const LocalStockListing = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [vendors, setVendors] = useState([]);
  const [vendor, setVendor] = useState(null);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [activeProcurement, setActiveProcurement] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Pagination (MUI Pagination style)
  const itemsPerPage = 20;
  const [pagination, setPagination] = useState({
    totalDocuments: 0,
    limit: itemsPerPage,
    NumberOfPages: 1,
    currentPage: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const fetchVendors = async () => {
    try {
      const res = await axios.get(`${API.BASE_URL}vendor`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVendors(res?.data?.data?.vendors || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch vendors for filter.");
    }
  };

  const fetchLocalProcurements = async (page = 1) => {
    setLoading(true);

    try {
      const res = await axios.get(`${API.BASE_URL}local-procurement`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          outlet: outletData?._id,
          page,
          limit: itemsPerPage,
          vendor: vendor?._id || vendor || undefined,
          startDate: fromDate || undefined,
          endDate: toDate || undefined,
        },
      });

      const rows = res?.data?.data?.dbResponse || [];

      // If backend provides pagination, prefer it; else fallback to length-based
      const pag =
        res?.data?.pagination ||
        res?.data?.data?.pagination ||
        res?.data?.data?.dbResponse?.pagination ||
        {};

      const totalDocs = pag?.totalDocuments ?? rows.length;
      const numPages = pag?.NumberOfPages ?? Math.max(1, Math.ceil(totalDocs / itemsPerPage));

      setList(Array.isArray(rows) ? rows : []);
      setPagination({
        totalDocuments: totalDocs,
        limit: pag?.limit ?? itemsPerPage,
        NumberOfPages: numPages,
        currentPage: pag?.currentPage ?? page,
      });
      setCurrentPage(pag?.currentPage ?? page);
    } catch (err) {
      console.error(err);
      setList([]);
      toast.error(err?.response?.data?.data?.message || "Failed to fetch local procurements.");
      setPagination({
        totalDocuments: 0,
        limit: itemsPerPage,
        NumberOfPages: 1,
        currentPage: 1,
      });
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchProcurementItems = async (procurementId) => {
    setItemsLoading(true);
    setItems([]);
    try {
      const res = await axios.get(`${API.BASE_URL}local-procurement-item`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { localProcurementId: procurementId },
      });

      const rows = res?.data?.data?.dbResponse || [];
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || err?.message || "Failed to fetch items.");
    } finally {
      setItemsLoading(false);
    }
  };

  const handleViewItems = (row) => {
    setActiveProcurement(row);
    setItemsDialogOpen(true);
    fetchProcurementItems(row._id);
  };

  const handleCloseItemsDialog = () => {
    setItemsDialogOpen(false);
    setActiveProcurement(null);
    setItems([]);
  };

  const handleAskDeleteProcurement = (row) => {
    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteProcurement = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);

      await axios.delete(`${API.BASE_URL}local-procurement`, {
        data: { _id: deleteTarget._id },
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success(
        `Local procurement with Invoice #${deleteTarget.invoiceNo || ""} deleted successfully.`
      );

      setList((prev) => prev.filter((p) => p._id !== deleteTarget._id));
      setPagination((prev) => ({
        ...prev,
        totalDocuments: Math.max((prev.totalDocuments || 0) - 1, 0),
      }));

      setDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.error || err?.message || "Failed to delete local procurement.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCloseDeleteDialog = () => {
    if (deleteLoading) return;
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleDownloadInvoice = (row) => {
    const url = row.invoice_file;
    if (!url) {
      toast.error("No invoice file found for this procurement.");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = url;

      const baseName = row.invoiceNo ? `invoice-${row.invoiceNo}` : "invoice";
      const lastPart = url.split("/").pop() || "";
      const extMatch = lastPart.match(/\.[^.?]+/);
      const ext = extMatch ? extMatch[0] : "";

      link.download = `${baseName}${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error(e);
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleGet = () => {
    // basic date validation
    if (fromDate && toDate && fromDate > toDate) {
      toast.error("From Date cannot be greater than To Date");
      return;
    }
    fetchLocalProcurements(1);
  };

  const handleReset = () => {
    setSearch("");
    setVendor(null);
    setFromDate("");
    setToDate("");
    setCurrentPage(1);
    fetchLocalProcurements(1);
    toast.info("Filters cleared.");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-IN");
  };

  const formatMoney = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // const computeItemAmount = (item) => {
  //   if (item.amount !== undefined && item.amount !== null) return Number(item.amount);
  //   const q = Number(item.quantity || 0);
  //   const r = Number(item.rate || 0);
  //   return q * r;
  // };

  // Local search filter (client-side)
  const filteredList = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = Array.isArray(list) ? list : [];
    if (!term) return rows;

    return rows.filter((row) => {
      const invoiceNo = (row.invoiceNo || row.invoice_no || "").toString();
      const vendorName = (row.vendor?.name || row.vendorName || row.vendor_name || "").toString();
      const amountStr = (
        row.grandTotal ??
        row.totalAmount ??
        row.total ??
        row.amount ??
        row.totalTaxable ??
        ""
      ).toString();

      return (
        invoiceNo.toLowerCase().includes(term) ||
        vendorName.toLowerCase().includes(term) ||
        amountStr.toLowerCase().includes(term)
      );
    });
  }, [list, search]);

  const totalRecords = filteredList.length;

  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    fetchLocalProcurements(page);
  };

  useEffect(() => {
    fetchVendors();
    fetchLocalProcurements(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Box sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
        <Toolbar />

        <Grid container justifyContent="center" sx={{ pb: 6 }}>
          <Grid item xs={12} md={11}>
            {/* Header */}
            <Card
              elevation={0}
              sx={{
                mt: 4,
                borderRadius: 3,
                border: "1px solid #e6eaf0",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
              }}
            >
              <CardContent sx={{ py: 3 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  spacing={2}
                >
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <KeyboardArrowLeft
                      onClick={() => navigate(-1)}
                      fontSize="large"
                      sx={{ cursor: "pointer" }}
                    />

                    <Box
                      sx={(theme) => ({
                        width: 46,
                        height: 46,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: theme.palette.primary.light + "22",
                        color: theme.palette.primary.main,
                      })}
                    >
                      <LocalMallOutlined />
                    </Box>

                    <Box>
                      <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                        Local Procurement List
                      </Typography>
                      <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                        View all local procurements created for this outlet. Use filters to narrow
                        down the records.
                      </Typography>
                    </Box>
                  </Stack>

                  <Chip
                    icon={<FilterAltOutlined />}
                    label={`Records: ${totalRecords}`}
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                    variant="outlined"
                  />
                </Stack>
              </CardContent>
            </Card>

            <Card
              elevation={0}
              sx={{
                mt: 2,
                borderRadius: 3,
                border: "1px solid #e6eaf0",
                background: "#fff",
              }}
            >
              <CardContent sx={{ py: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                      Vendor
                    </Typography>

                    <Autocomplete
                      size="small"
                      fullWidth
                      options={vendors}
                      getOptionLabel={(option) => option?.name || ""}
                      value={vendor}
                      onChange={(e, newVal) => setVendor(newVal)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="All"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              height: 52,
                              background: "#fff",
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                      From Date
                    </Typography>

                    <TextField
                      fullWidth
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          height: 52,
                          background: "#fff",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                      To Date
                    </Typography>

                    <TextField
                      fullWidth
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          height: 52,
                          background: "#fff",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                      Search
                    </Typography>

                    <TextField
                      fullWidth
                      placeholder="Invoice / Vendor / Amount"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          height: 52,
                          background: "#fff",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRounded />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "#334155",
                        mb: 0.8,
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Button
                        variant="contained"
                        disabled={loading}
                        onClick={handleGet}
                        sx={{
                          height: 52,
                          width: "70%",
                          borderRadius: 2,
                          fontWeight: 800,
                          textTransform: "none",
                          backgroundColor: "#003049",
                          "&:hover": { backgroundColor: "#00263a" },
                        }}
                      >
                        {loading ? "Loading..." : "Get"}
                      </Button>

                      <IconButton
                        onClick={handleReset}
                        disabled={loading}
                        sx={{
                          height: 52,
                          width: 52,
                          borderRadius: 2,
                          border: "1px solid #e6eaf0",
                          background: "#fff",
                        }}
                        title="Reset Filters"
                      >
                        <RefreshIcon sx={{ color: "#003049" }} />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card
              elevation={0}
              sx={{
                mt: 2,
                borderRadius: 3,
                border: "1px solid #e6eaf0",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                {loading ? (
                  <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                    <CircularProgress />
                    <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching procurements...</Typography>
                  </Box>
                ) : filteredList.length === 0 ? (
                  <Box sx={{ py: 6, textAlign: "center" }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                      No local procurements found
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                      Change filters and click “Get” to view results.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${filteredList.length}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />

                        <Chip
                          label={`Page: ${currentPage} / ${pagination?.NumberOfPages || 1}`}
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                          variant="outlined"
                        />
                      </Stack>
                    </Box>

                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        borderRadius: 0,
                        maxHeight: "70vh",
                        overflow: "auto",
                      }}
                    >
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            {["No.", "Date", "Invoice No.", "Vendor", "Amount (₹)", "Actions"].map((h) => (
                              <TableCell
                                key={h}
                                sx={{
                                  fontWeight: 900,
                                  color: "#0f172a",
                                  background: "#f1f5f9",
                                  borderBottom: "1px solid #e2e8f0",
                                  whiteSpace: "nowrap",
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 2,
                                }}
                                align={h === "Vendor" || h === "Invoice No." ? "left" : h === "Actions" ? "center" : "center"}
                              >
                                {h}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {[...filteredList]
                            .slice()
                            .reverse()
                            .map((row, idx) => {
                              const createdAt = row.createdAt || row.date || row.created_on;
                              const invoiceNo = row.invoiceNo || row.invoice_no || "-";
                              const vendorName = row.vendor?.name || row.vendorName || row.vendor_name || "-";
                              const amount = row.grandTotal ?? 0;
                              const serial = (currentPage - 1) * itemsPerPage + (idx + 1);

                              return (
                                <TableRow
                                  key={row._id || idx}
                                  hover
                                  sx={{
                                    backgroundColor: idx % 2 === 0 ? "#ffffff" : "#EDF9FF",
                                    "& td": { borderBottom: "1px solid #f1f5f9" },
                                  }}
                                >
                                  <TableCell align="center" sx={{ fontWeight: 600, color: "#334155" }}>
                                    {serial}
                                  </TableCell>

                                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                                    {formatDate(createdAt)}
                                  </TableCell>

                                  <TableCell align="left" sx={{ fontWeight: 600, color: "#0f172a" }}>
                                    {invoiceNo}
                                  </TableCell>

                                  <TableCell align="left">
                                    <Chip
                                      label={vendorName}
                                      size="small"
                                      sx={{ fontWeight: 600, borderRadius: 2, maxWidth: 220 }}
                                      variant="outlined"
                                    />
                                  </TableCell>

                                  <TableCell align="center" sx={{ fontWeight: 600, color: "#0f172a" }}>
                                    ₹{formatMoney(amount)}
                                  </TableCell>

                                  <TableCell align="center">
                                    <Stack direction="row" spacing={1} justifyContent="center">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleViewItems(row)}
                                        sx={{
                                          backgroundColor: "#e3f2fd",
                                          "&:hover": { backgroundColor: "#bbdefb" },
                                        }}
                                        title="View Items"
                                      >
                                        <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#1565c0" }} />
                                      </IconButton>

                                      <IconButton
                                        size="small"
                                        onClick={() => handleDownloadInvoice(row)}
                                        sx={{
                                          backgroundColor: "#d1f7d6",
                                          "&:hover": { backgroundColor: "#b2e8b5" },
                                        }}
                                        title="Download Invoice"
                                      >
                                        <DownloadIcon fontSize="small" sx={{ color: "#2e7d32" }} />
                                      </IconButton>

                                      <IconButton
                                        size="small"
                                        onClick={() => handleAskDeleteProcurement(row)}
                                        sx={{
                                          backgroundColor: "#fdecea",
                                          "&:hover": { backgroundColor: "#f9d6d2" },
                                        }}
                                        title="Delete"
                                      >
                                        <DeleteOutlineIcon fontSize="small" sx={{ color: "#d32f2f" }} />
                                      </IconButton>
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Divider />

                    <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                      <Pagination
                        count={pagination?.NumberOfPages || 1}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        variant="outlined"
                        shape="rounded"
                      />
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={itemsDialogOpen}
        onClose={handleCloseItemsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f9fb" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 2,
            background: "linear-gradient(90deg, #003049, #274c77)",
            color: "white",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Procurement Items
            </Typography>
            {activeProcurement && (
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Invoice #{activeProcurement.invoiceNo} • {activeProcurement.vendor?.name}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleCloseItemsDialog} size="small" sx={{ color: "white" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2 }}>
          {itemsLoading ? (
            <Box sx={{ py: 5, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <CircularProgress size={26} />
              <Typography variant="body2" sx={{ mt: 1 }}>
                Loading items...
              </Typography>
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary" }}>
              <Typography>No items found for this procurement.</Typography>
            </Box>
          ) : (
            <Box
              sx={{
                mt: 1,
                borderRadius: 2,
                border: "1px solid #e0e6ec",
                overflow: "hidden",
                backgroundColor: "white",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f1f3f5" }}>
                    <TableCell sx={{ fontWeight: 900 }}>Sr.</TableCell>
                    <TableCell sx={{ fontWeight: 900 }}>Item</TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">
                      Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: 900 }} align="right">
                      Amount (₹)
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {items.map((item, idx) => {
                    const rmName = item?.rawMaterial?.name || "Unnamed";
                    return (
                      <TableRow
                        key={idx}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafcff" },
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{rmName}</TableCell>
                        <TableCell align="right">
                          {Number(item.quantity || 0).toFixed(2)} {item?.rawMaterial?.unit}
                        </TableCell>
                        <TableCell align="right">{item?.grandTotal.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={handleCloseItemsDialog} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1, backgroundColor: "#fffaf4" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 900, color: "#b33a3a" }}>
          ⚠️ Delete Procurement
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" fontWeight={800}>
            Are you sure you want to delete this record?
          </Typography>

          {deleteTarget && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, backgroundColor: "#fff" }}>
              <Typography variant="body2">
                <strong>Invoice:</strong> {deleteTarget.invoiceNo}
              </Typography>
              <Typography variant="body2">
                <strong>Vendor:</strong> {deleteTarget.vendor?.name || "Unknown"}
              </Typography>
            </Box>
          )}

          <Typography variant="caption" sx={{ mt: 1.5, color: "#b33a3a", display: "block" }}>
            This action cannot be undone. All related data will be permanently removed.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleCloseDeleteDialog} disabled={deleteLoading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteProcurement}
            disabled={deleteLoading}
            sx={{ fontWeight: 900, px: 3, textTransform: "none" }}
          >
            {deleteLoading ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LocalStockListing;
